import {
  AirplanemodeActiveTwoTone,
  FilterAltTwoTone,
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ITourCustomerResult } from '../utils/types';
import GeneralCustomerListFilterDialog from './GeneralCustomerListFilterDialog';
import { AddTransportationRequisitionDialog } from './HtmlContent/AddTransportationRequisitionsDialog';

type GuideCustomerTransportationListDialogProps = {
  tourCustomerResult: ITourCustomerResult[];
  open: boolean;
  onClose: () => void;
  guideSalesIndividualId: string;
};

export default function GuideCustomerTransportationListDialog({
  tourCustomerResult,
  open,
  onClose,
  guideSalesIndividualId,
}: GuideCustomerTransportationListDialogProps) {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const [openFilter, setOpenFilter] = useState(false);
  const [selectedSalesGroups, setSelectedSalesGroups] = useState<string[]>([]);
  const [selectedTourGroups, setSelectedTourGroups] = useState<string[]>([]);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [dialogDirection, setDialogDirection] = useState<
    'DEPARTURE' | 'RETURN'
  >('DEPARTURE');
  const [selectedSalesIndividualId, setSelectedSalesIndividualId] =
    useState<number>(0);

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleAdd = (
    direction: 'DEPARTURE' | 'RETURN',
    salesIndividualId: number,
  ) => {
    setDialogDirection(direction);
    setSelectedSalesIndividualId(salesIndividualId);
    setOpenDialog(true);
  };

  const handleSaveTransportation = async () => {
    setOpenDialog(false);
    // await fetchTransportationInfos(selectedSalesIndividualId);
  };

  const tourGroupOptions = Array.from(
    new Set(
      tourCustomerResult
        .filter(
          (row) => row?.tourGroup !== undefined && row?.tourGroup !== null,
        )
        .map((row) => row.tourGroup),
    ),
  );

  const salesGroupOptions = Array.from(
    new Set(
      tourCustomerResult
        .filter(
          (row) =>
            row?.salesOrderGroup !== undefined && row?.salesOrderGroup !== null,
        )
        .map((row) => row.salesOrderGroup),
    ),
  );

  return (
    <Dialog sx={{ opacity: 1 }} fullScreen open={open} onClose={onClose}>
      <DialogTitle sx={{ display: 'flex' }}>
        {t('guideTransportationDialog.title')}
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" alignItems="center" sx={{ mb: 2, pt: 0.5 }}>
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={() => setOpenFilter(!openFilter)} sx={{ mr: 1 }}>
            <FilterAltTwoTone />
          </IconButton>

          <TextField
            label={t('guideTourAccommodationInfo.searchPerson')}
            placeholder={t('guideTourAccommodationInfo.searchWithName')}
            variant="outlined"
            color="secondary"
            size="small"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon color="secondary" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <GeneralCustomerListFilterDialog
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          tourGroupOptions={tourGroupOptions}
          salesGroupOptions={salesGroupOptions}
          selectedTourGroups={selectedTourGroups}
          setSelectedTourGroups={setSelectedTourGroups}
          selectedSalesGroups={selectedSalesGroups}
          setSelectedSalesGroups={setSelectedSalesGroups}
        />

        <TableContainer component={Paper}>
          <Table aria-label="pilgrims table">
            <TableHead
              sx={{
                backgroundColor: 'secondary.main',
                th: {
                  color: 'secondary.contrastText',
                },
              }}
            >
              <TableRow>
                <TableCell align="left">
                  {t('guideGeneralCustomerList.customerName')}
                </TableCell>
                <TableCell align="center">
                  {t('guideTransportationDialog.loyaltyNumber')}
                </TableCell>
                <TableCell align="center">
                  {t('guideTransportationDialog.PNR')}
                </TableCell>
                <TableCell align="center">
                  {t('guideTransportationDialog.domesticArrival')}
                </TableCell>
                <TableCell align="center">
                  {t('guideTransportationDialog.domesticReturn')}
                </TableCell>
                <TableCell align="center">
                  {t('guideTransportationDialog.salesOffice')}
                </TableCell>
                <TableCell align="center">
                  {t('guideTransportationDialog.tourGroup')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tourCustomerResult
                .filter((row) => {
                  const fullName =
                    `${row.firstName} ${row.lastName}`.toLowerCase();
                  const searchLowerCase = searchValue.toLowerCase();

                  return (
                    fullName.includes(searchLowerCase) &&
                    (selectedTourGroups.length === 0 ||
                      selectedTourGroups.includes(row.tourGroup)) &&
                    (selectedSalesGroups.length === 0 ||
                      selectedSalesGroups.includes(row.salesOrderGroup))
                  );
                })
                .map((row, index: number) => (
                  <TableRow key={index}>
                    <TableCell scope="row" align="left">
                      {row.firstName} {row.lastName}
                    </TableCell>

                    <TableCell scope="row" align="center">
                      {row.loyaltyNumber || '-'}
                    </TableCell>
                    <TableCell scope="row" align="center">
                      {row.pnr || '-'}
                    </TableCell>
                    <TableCell scope="row" align="center">
                      {row.domesticArrival || '-'}
                      <IconButton
                        color="secondary"
                        onClick={() =>
                          handleAdd('DEPARTURE', row.salesIndividualId)
                        }
                      >
                        <AirplanemodeActiveTwoTone />
                      </IconButton>
                    </TableCell>
                    <TableCell scope="row" align="center">
                      {row.domesticReturn || '-'}
                      <IconButton
                        color="secondary"
                        onClick={() =>
                          handleAdd('RETURN', row.salesIndividualId)
                        }
                      >
                        <AirplanemodeActiveTwoTone />
                      </IconButton>
                    </TableCell>
                    <TableCell scope="row" align="center">
                      {row.salesOrderGroup || '-'}
                    </TableCell>
                    <TableCell scope="row" align="center">
                      {row.tourGroup || '-'}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <AddTransportationRequisitionDialog
        open={openDialog}
        onClose={handleDialogClose}
        onSave={handleSaveTransportation}
        salesIndividualId={selectedSalesIndividualId}
        direction={dialogDirection}
        guideSalesIndividualId={guideSalesIndividualId}
      />
    </Dialog>
  );
}
